import React, { useState } from 'react';
import { SharedLink } from './models/shared-link.model';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MoreVertical, Copy, Edit, Trash2, Eye, EyeOff } from 'lucide-react';
import { useI18n } from '@/hooks/useI18n';
import { useToast } from '@/hooks/useToast';
import { useNavigate } from 'react-router-dom';

interface SharedLinksTableProps {
  links: SharedLink[];
  onEdit: (link: SharedLink) => void;
  onDelete: (id: string) => void;
}

export const SharedLinksTable: React.FC<SharedLinksTableProps> = ({
  links,
  onEdit,
  onDelete,
}) => {
  const { t } = useI18n();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState<Record<string, boolean>>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLinkId, setSelectedLinkId] = useState<string | null>(null);

  const togglePasswordVisibility = (id: string) => {
    setShowPassword((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const copyToClipboard = (link: SharedLink) => {
    const shareUrl = `${window.location.origin}/#/shared/${link.token}`;
    navigator.clipboard.writeText(shareUrl);
    toast({
      title: t('shared.linkCopied'),
      description: shareUrl,
    });
  };

  const handleDeleteClick = (id: string) => {
    setSelectedLinkId(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (selectedLinkId) {
      await onDelete(selectedLinkId);
    }
    setDeleteDialogOpen(false);
    setSelectedLinkId(null);
  };

  const handleItineraryClick = (itineraryId: string) => {
    navigate(`/itinerary-builder?itineraryId=${itineraryId}`);
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <TooltipProvider>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[18%]">{t('shared.itinerary')}</TableHead>
              <TableHead className="w-[16%]">{t('shared.users')}</TableHead>
              <TableHead className="w-[10%]">{t('shared.password')}</TableHead>
              <TableHead className="w-[14%]">{t('shared.expiration')}</TableHead>
              <TableHead className="w-[32%]">{t('shared.link')}</TableHead>
              <TableHead className="text-right w-[10%]">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {links.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  {t('shared.noLinks')}
                </TableCell>
              </TableRow>
            ) : (
              links.map((link) => {
                const shareUrl = `${window.location.origin}/#/shared/${link.token}`;
                return (
                  <TableRow key={link.id}>
                    {/* Itinerary Name */}
                    <TableCell>
                      <button
                        onClick={() => handleItineraryClick(link.itinerary_id)}
                        className="text-blue-600 hover:text-blue-800 hover:underline font-medium text-left"
                      >
                        {link.itinerary_name}
                      </button>
                    </TableCell>

                    {/* Users (Client + Advisor) */}
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        {link.client_name ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center gap-1">
                                <Badge variant="outline" className="cursor-help text-xs">
                                  {link.client_name}
                                </Badge>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="text-xs">Cliente: {link.client_email}</p>
                            </TooltipContent>
                          </Tooltip>
                        ) : null}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex items-center gap-1">
                              <Badge variant="secondary" className="cursor-help text-xs">
                                {link.designer_name}
                              </Badge>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs">Advisor: {link.designer_email}</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </TableCell>

                    {/* Password */}
                    <TableCell>
                      {link.password ? (
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm">
                            {showPassword[link.id] ? link.password : '••••••••'}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => togglePasswordVisibility(link.id)}
                            className="h-6 w-6 p-0"
                          >
                            {showPassword[link.id] ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      ) : (
                        <span className="text-muted-foreground italic">-</span>
                      )}
                    </TableCell>

                    {/* Expiration */}
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        {link.expires_at ? (
                          <>
                            <span className="text-sm">
                              {formatDate(new Date(link.expires_at))}
                            </span>
                            <Badge 
                              variant={link.is_active ? 'default' : 'destructive'}
                              className="w-fit text-xs"
                            >
                              {link.is_active ? t('shared.active') : t('shared.expired')}
                            </Badge>
                          </>
                        ) : (
                          <span className="text-muted-foreground italic text-sm">
                            {t('shared.noExpiration')}
                          </span>
                        )}
                      </div>
                    </TableCell>

                    {/* Share Link */}
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="font-mono text-xs text-muted-foreground truncate max-w-[250px] block cursor-help">
                              {shareUrl}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-md">
                            <p className="font-mono text-xs break-all">{shareUrl}</p>
                          </TooltipContent>
                        </Tooltip>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyToClipboard(link)}
                          className="h-7 px-2 shrink-0"
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>

                    {/* Actions */}
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => onEdit(link)}>
                            <Edit className="mr-2 h-4 w-4" />
                            {t('common.edit')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDeleteClick(link.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            {t('common.delete')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('shared.deleteLink')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('shared.confirmDelete')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
                {t('common.delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </TooltipProvider>
  );
};
